import { useCallback, useState } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client";
import { GroupsPagination, INITIAL_CURSOR } from "../api/cache/GroupsPagination";
import { createPin } from "../api/mutations/createPin";
import { type CreatePinMutation, type CreatePinMutationVariables } from "../api/mutations/createPin.gql-generated";
import { removePin } from "../api/mutations/removePin";
import { type RemovePinMutation, type RemovePinMutationVariables } from "../api/mutations/removePin.gql-generated";
import { GroupsGridItemModel } from "../grid/model";

/**
 * Is used to pin/unpin a group. Invalidates cache once mutation is done.
 * @param refetchLimit How many groups to request when requesting fresh data.
 */
function useGroupPinMutation(refetchLimit: number) {
    // track a group that is currently being pinned
    const [groupBeingPinned, setGroupBeingPinned] = useState<string | undefined>();

    const [createPinGroupMutation] = useMutation<CreatePinMutation, CreatePinMutationVariables>(createPin);
    const [removePinGroupMutation] = useMutation<RemovePinMutation, RemovePinMutationVariables>(removePin);

    const pinGroup = useCallback(
        (groupId: string, toPin: boolean) => {
            setGroupBeingPinned(groupId);
            const createOrRemovePin = toPin ? createPinGroupMutation : removePinGroupMutation;
            createOrRemovePin({
                variables: { groupId },
                refetchQueries: [GroupsPagination.query],
                onQueryUpdated(observableQuery) {
                    // re-fetch all groups we have so far, as their order and pin status might have changed
                    // NOTE: "onQueryUpdated" is called for each query specified in "refetchQueries"
                    return observableQuery
                        .fetchMore({
                            variables: {
                                limit: refetchLimit,
                                cursor: INITIAL_CURSOR,
                            },
                        })
                        .finally(() => {
                            setGroupBeingPinned(undefined);
                        });
                },
            });
        },
        [refetchLimit, createPinGroupMutation, removePinGroupMutation],
    );

    return {
        pinGroup,
        groupBeingPinned,
    };
}

/**
 * Queries Lens Groups using cursor+limit pagination.
 * The cache is handled by getGroupListV2CacheTypePolicy type policy.
 */
export function usePinnedGroups(pageSize = 10) {
    // load first page right away
    const { orgId } = useParams<{ orgId?: string }>();
    const {
        data,
        loading,
        error,
        fetchMore: fetchNextPage,
    } = useQuery(GroupsPagination.query, {
        variables: {
            limit: pageSize,
            cursor: INITIAL_CURSOR,
            organizationId: orgId,
        },
        skip: orgId === undefined,
    });

    // "data" always has groups combined from all pages requested so far
    // "cursor" and "hasMore" come from the last page response only
    const { cursor, hasMore, groupsWithConnections } = data?.getGroupListV2 ?? {};

    const fetchMore = useCallback(() => {
        if (cursor) {
            fetchNextPage({
                variables: {
                    cursor,
                },
            });
        }
    }, [cursor, fetchNextPage]);

    const { pinGroup, groupBeingPinned } = useGroupPinMutation(groupsWithConnections?.length ?? pageSize);

    return {
        groups: groupsWithConnections?.map(GroupsGridItemModel.createFromGroupWithConnections) ?? [],
        hasMore: hasMore ?? true,
        loading,
        error,
        fetchMore,
        pinGroup,
        groupBeingPinned,
    };
}
